/**
 * Modal component to display lifestyle parameter question and answer details
 * Shows user-friendly question text and answer in the current UI language
 * Can display either a single parameter or multiple parameters from a timestamp
 */

import React, { useMemo } from 'react';
import { Modal, Descriptions, Typography, Tag, Space, Divider, Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getLifestyleParameterDisplayInfo } from '../utils/lifestyleQuestionDisplay';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const LifestyleParameterDetailModal = ({
  visible,
  onClose,
  parameterKey,
  value,
  timestamp,
  notes,
  parameters // Array of { parameterKey, value, notes } for multi-parameter view
}) => {
  const { t } = useTranslation();

  // Determine if this is single or multi-parameter mode
  const isSingleMode = !parameters && parameterKey;
  const isMultiMode = parameters && Array.isArray(parameters) && parameters.length > 0;

  // Get display information for single parameter mode
  const singleDisplayInfo = useMemo(() => {
    if (isSingleMode && visible && parameterKey) {
      return getLifestyleParameterDisplayInfo(parameterKey, value, timestamp, t);
    }
    return null;
  }, [isSingleMode, visible, parameterKey, value, timestamp, t]);

  // Get display information for multi-parameter mode, grouped by category
  const groupedParameters = useMemo(() => {
    if (!isMultiMode || !visible) {
      return {};
    }

    const groups = {};
    parameters.forEach(param => {
      const displayInfo = getLifestyleParameterDisplayInfo(
        param.parameterKey,
        param.value,
        timestamp,
        t
      );

      if (displayInfo && displayInfo.category) {
        if (!groups[displayInfo.category]) {
          groups[displayInfo.category] = [];
        }
        groups[displayInfo.category].push({
          ...displayInfo,
          notes: param.notes
        });
      }
    });

    return groups;
  }, [isMultiMode, visible, parameters, timestamp, t]);

  if (!visible || (!isSingleMode && !isMultiMode)) {
    return null;
  }

  // Render single parameter view
  if (isSingleMode && singleDisplayInfo) {
    return (
      <Modal
        title={
          <Space direction="vertical" size={0}>
            <Title level={4} style={{ margin: 0 }}>
              {t('flowsheet.lifestyleParameterDetails', { defaultValue: 'Lifestyle Parameter Details' })}
            </Title>
            {singleDisplayInfo.category && (
              <Tag color="blue">{singleDisplayInfo.category}</Tag>
            )}
          </Space>
        }
        open={visible}
        onCancel={onClose}
        footer={null}
        width={600}
      >
        <Descriptions
          bordered
          column={1}
          size="middle"
          labelStyle={{ fontWeight: 'bold', width: '30%' }}
        >
          <Descriptions.Item
            label={t('flowsheet.question', { defaultValue: 'Question' })}
          >
            <Text>{singleDisplayInfo.question}</Text>
          </Descriptions.Item>

          <Descriptions.Item
            label={t('flowsheet.answer', { defaultValue: 'Answer' })}
          >
            <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
              {singleDisplayInfo.answer}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item
            label={t('flowsheet.timestamp', { defaultValue: 'Date & Time' })}
          >
            <Text>
              {timestamp ? moment(timestamp).format('YYYY-MM-DD HH:mm') : 'N/A'}
            </Text>
          </Descriptions.Item>

          {notes && (
            <Descriptions.Item
              label={t('flowsheet.notes', { defaultValue: 'Notes' })}
            >
              <Text type="secondary">{notes}</Text>
            </Descriptions.Item>
          )}

          <Descriptions.Item
            label={t('flowsheet.technicalInfo', { defaultValue: 'Technical Info' })}
          >
            <Space direction="vertical" size="small">
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <strong>{t('flowsheet.parameterKey', { defaultValue: 'Parameter Key' })}:</strong> {singleDisplayInfo.parameterKey}
              </Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <strong>{t('flowsheet.rawValue', { defaultValue: 'Raw Value' })}:</strong> {String(singleDisplayInfo.rawValue)}
              </Text>
            </Space>
          </Descriptions.Item>
        </Descriptions>
      </Modal>
    );
  }

  // Render multi-parameter view
  if (isMultiMode) {
    const categoryKeys = Object.keys(groupedParameters).sort();
    const totalParameters = parameters.length;

    return (
      <Modal
        title={
          <Space direction="vertical" size={0}>
            <Title level={4} style={{ margin: 0 }}>
              {t('flowsheet.lifestyleAssessmentSnapshot', { defaultValue: 'Lifestyle Assessment Snapshot' })}
            </Title>
            <Space>
              <Text type="secondary">
                {timestamp ? moment(timestamp).format('YYYY-MM-DD HH:mm') : 'N/A'}
              </Text>
              <Tag color="blue">
                {t('flowsheet.parametersCount', {
                  defaultValue: '{{count}} parameters',
                  count: totalParameters
                })}
              </Tag>
            </Space>
          </Space>
        }
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Collapse
          defaultActiveKey={categoryKeys}
          ghost
          style={{ background: '#fff' }}
        >
          {categoryKeys.map(category => (
            <Panel
              header={
                <Space>
                  <Tag color="blue">{category}</Tag>
                  <Text type="secondary">
                    ({groupedParameters[category].length} {t('flowsheet.parameters', { defaultValue: 'parameters' })})
                  </Text>
                </Space>
              }
              key={category}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {groupedParameters[category].map((param, index) => (
                  <div key={index} style={{
                    padding: '12px',
                    background: '#fafafa',
                    borderRadius: '4px',
                    border: '1px solid #f0f0f0'
                  }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong style={{ color: '#595959' }}>
                        {param.question}
                      </Text>
                      <Text style={{ fontSize: '16px', color: '#1890ff', fontWeight: 500 }}>
                        → {param.answer}
                      </Text>
                      {param.notes && (
                        <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                          {t('flowsheet.notes', { defaultValue: 'Notes' })}: {param.notes}
                        </Text>
                      )}
                    </Space>
                  </div>
                ))}
              </Space>
            </Panel>
          ))}
        </Collapse>
      </Modal>
    );
  }

  return null;
};

export default LifestyleParameterDetailModal;

